<template>
  <div class="tmp-panel">
    <div class="tmp-header">
      <template v-if="view === 'list'">
        <span class="tmp-title">Equipo de mantenimiento</span>
        <span class="tmp-counts">{{ activeCount }} activos · {{ availableCount }} disponibles · {{ workingCount }} en trabajo</span>
      </template>
      <template v-else>
        <button class="tmp-back-btn" @click="onBackFromForm"><span class="mdi mdi-arrow-left"></span></button>
        <span class="tmp-title">{{ formMode === 'edit' ? 'Editar técnico' : 'Nuevo técnico' }}</span>
      </template>
      <button class="tmp-close-btn" @click="$emit('close')"><span class="mdi mdi-close"></span></button>
    </div>

    <!-- ===== Vista de lista ===== -->
    <div v-if="view === 'list'" class="tmp-content">
      <div v-if="activeTechnicians.length === 0 && inactiveTechnicians.length === 0" class="tmp-empty">
        <span class="mdi mdi-account-group-outline tmp-empty-icon"></span>
        <span class="tmp-empty-title">Sin técnicos registrados</span>
        <span class="tmp-empty-sub">Agrega tu primer técnico para poder asignar tickets de mantenimiento</span>
      </div>

      <template v-else>
        <div v-for="t in activeTechnicians" :key="t.id" class="tmp-card" :class="`tmp-card--${t.status.toLowerCase()}`">
          <div class="tmp-card-avatar-wrap">
            <span class="tmp-card-avatar" :style="{ background: AVATAR_COLORS[t.avatarColor] || t.avatarColor }">{{ initials(t.name) }}</span>
            <span class="tmp-card-presence" :class="`tmp-card-presence--${t.status.toLowerCase()}`"></span>
          </div>

          <div class="tmp-card-info">
            <div class="tmp-card-name">{{ t.name }}</div>
            <div class="tmp-card-specialty">{{ specialtyLabel(t) }}</div>
          </div>

          <div class="tmp-card-metrics">
            <span class="tmp-card-count">{{ t.activeTicketCount }} tickets activos</span>
            <div class="tmp-card-capacity-bar">
              <div class="tmp-card-capacity-fill" :class="capacityClass(t.capacityPercent)" :style="{ width: t.capacityPercent + '%' }"></div>
            </div>
          </div>

          <div class="tmp-card-actions">
            <button class="tmp-card-menu-btn" @click="toggleRowMenu(t.id, $event)"><span class="mdi mdi-dots-vertical"></span></button>
          </div>
        </div>

        <template v-if="inactiveTechnicians.length">
          <div class="tmp-divider"><span>Técnicos inactivos</span></div>
          <div v-for="t in inactiveTechnicians" :key="t.id" class="tmp-card tmp-card--inactive">
            <div class="tmp-card-avatar-wrap">
              <span class="tmp-card-avatar" :style="{ background: AVATAR_COLORS[t.avatarColor] || t.avatarColor }">{{ initials(t.name) }}</span>
              <span class="tmp-card-presence tmp-card-presence--inactive"></span>
            </div>
            <div class="tmp-card-info">
              <div class="tmp-card-name">{{ t.name }}</div>
              <div class="tmp-card-specialty">{{ specialtyLabel(t) }}</div>
            </div>
            <div class="tmp-card-metrics">
              <span class="tmp-card-count">Inactivo</span>
            </div>
            <div class="tmp-card-actions">
              <button class="tmp-card-menu-btn" @click="toggleRowMenu(t.id, $event)"><span class="mdi mdi-dots-vertical"></span></button>
            </div>
          </div>
        </template>
      </template>

      <Teleport to="body">
        <div v-if="rowMenuOpenId" class="tmp-row-menu" :style="rowMenuPos">
          <button @click="onEditFromMenu">Editar</button>
          <button @click="onStub('Ver historial de tickets')">Ver historial de tickets</button>
          <button @click="onStub('Cambiar disponibilidad')">Cambiar disponibilidad</button>
          <button @click="onToggleActive">{{ rowMenuTechnician?.active ? 'Desactivar técnico' : 'Reactivar técnico' }}</button>
        </div>
      </Teleport>
    </div>

    <!-- ===== Formulario ===== -->
    <div v-else class="tmp-content">
      <section class="tmp-form-section">
          <span class="tmp-form-group-title">Identidad</span>
          <input v-model="form.name" class="tmp-input" placeholder="Nombre completo del técnico" />

          <select v-model="form.specialty" class="tmp-input">
            <option value="">Selecciona especialidad</option>
            <option value="ELECTRICAL">Electricidad</option>
            <option value="PLUMBING">Plomería</option>
            <option value="HVAC">HVAC y climatización</option>
            <option value="CIVIL_WORK">Obra civil</option>
            <option value="FIRE_SYSTEMS">Sistemas contra incendios</option>
            <option value="GENERAL">General</option>
            <option value="OTHER">Otro</option>
          </select>
          <input v-if="form.specialty === 'OTHER'" v-model="form.specialtyOther" class="tmp-input" placeholder="Especifica la especialidad" />

          <input v-model="form.phone" class="tmp-input" placeholder="Teléfono de contacto" />
          <input v-model="form.email" class="tmp-input" placeholder="Correo electrónico" />

          <div class="tmp-field-row">
            <span class="tmp-field-label">Color del avatar</span>
            <div class="tmp-color-picker">
              <button
                v-for="c in AVATAR_COLOR_KEYS"
                :key="c"
                class="tmp-color-swatch"
                :class="{ 'tmp-color-swatch--active': form.avatarColor === c }"
                :style="{ background: AVATAR_COLORS[c] }"
                @click="form.avatarColor = c"
              ></button>
            </div>
          </div>
        </section>

      <section class="tmp-form-section">
        <span class="tmp-form-group-title">Configuración operativa</span>

        <div class="tmp-field-row">
          <span class="tmp-field-label">Capacidad máxima de tickets simultáneos</span>
          <div class="tmp-stepper">
            <button @click="form.maxConcurrentTickets = Math.max(1, form.maxConcurrentTickets - 1)">−</button>
            <span>{{ form.maxConcurrentTickets }}</span>
            <button @click="form.maxConcurrentTickets = Math.min(10, form.maxConcurrentTickets + 1)">+</button>
          </div>
        </div>

        <div class="tmp-pills">
          <button class="tmp-pill" :class="{ 'tmp-pill--active': form.contractType === 'EMPLOYEE' }" @click="form.contractType = 'EMPLOYEE'">Empleado directo</button>
          <button class="tmp-pill" :class="{ 'tmp-pill--active': form.contractType === 'EXTERNAL' }" @click="form.contractType = 'EXTERNAL'">Proveedor externo</button>
        </div>

        <div class="tmp-field-row">
          <span class="tmp-field-label">Tarifa por hora (MXN)</span>
          <input v-model.number="form.hourlyRate" type="number" class="tmp-input" />
        </div>

        <div class="tmp-field-row">
          <span class="tmp-field-label">Días disponibles</span>
          <div class="tmp-days">
            <button
              v-for="d in DAYS"
              :key="d.value"
              class="tmp-day-btn"
              :class="{ 'tmp-day-btn--active': form.availableDays.includes(d.value) }"
              @click="toggleDay(d.value)"
            >
              {{ d.label }}
            </button>
          </div>
        </div>

        <label class="tmp-toggle-row">
          <input type="checkbox" v-model="form.flexibleSchedule" /> Horario flexible
        </label>

        <div v-if="!form.flexibleSchedule" class="tmp-field-row-2col">
          <input v-model="form.availableStartTime" type="time" class="tmp-input" />
          <input v-model="form.availableEndTime" type="time" class="tmp-input" />
        </div>
      </section>

      <section class="tmp-form-section">
        <span class="tmp-form-group-title">Acceso al sistema</span>
        <label class="tmp-toggle-row">
          <input type="checkbox" v-model="form.platformAccessEnabled" /> Dar acceso a la plataforma
        </label>

        <template v-if="form.platformAccessEnabled">
          <input v-model="form.platformUsername" class="tmp-input" placeholder="Usuario" />
          <span class="tmp-hint">El técnico recibirá un correo con sus credenciales de acceso y un link para establecer su propia contraseña.</span>
        </template>
      </section>

      <section class="tmp-form-section">
        <span class="tmp-form-group-title">Notas internas</span>
        <textarea v-model="form.notes" class="tmp-textarea" rows="3" placeholder="Notas sobre este técnico visibles solo para el equipo administrativo..."></textarea>
      </section>
    </div>

    <!-- Barra inferior -->
    <div class="tmp-footer">
      <template v-if="view === 'list'">
        <button class="tmp-add-btn" @click="onOpenAddForm">＋ Agregar técnico nuevo</button>
      </template>
      <template v-else>
        <button class="tmp-cancel-btn" @click="onBackFromForm">Cancelar</button>
        <button class="tmp-save-btn" :disabled="!canSave" @click="onSave">Guardar técnico</button>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { getTechniciansForManager, createTechnician, updateTechnician } from '@/services/maintenance'

const props = defineProps({
  parkId: { type: String, required: true },
})

const emit = defineEmits(['close', 'updated'])

const SPECIALTY_LABELS = { ELECTRICAL: 'Electricidad', PLUMBING: 'Plomería', HVAC: 'HVAC y climatización', CIVIL_WORK: 'Obra civil', FIRE_SYSTEMS: 'Sistemas contra incendios', GENERAL: 'General' }
const DAYS = [
  { value: 'L', label: 'L' }, { value: 'M', label: 'M' }, { value: 'X', label: 'X' },
  { value: 'J', label: 'J' }, { value: 'V', label: 'V' }, { value: 'S', label: 'S' }, { value: 'D', label: 'D' },
]

const view = ref('list')
const formMode = ref('add')
const editingId = ref(null)
const technicians = ref([])

const AVATAR_COLORS = { blue: '#3b82f6', green: '#10b981', violet: '#8b5cf6', orange: '#f97316', teal: '#14b8a6', pink: '#ec4899', indigo: '#6366f1', amber: '#f0a500' }
const AVATAR_COLOR_KEYS = Object.keys(AVATAR_COLORS)

const emptyForm = () => ({
  name: '', cargo: '', avatarColor: 'blue', specialty: '', specialtyOther: '', phone: '', email: '',
  maxConcurrentTickets: 3, contractType: null, hourlyRate: null,
  availableDays: [], availableStartTime: '', availableEndTime: '', flexibleSchedule: false,
  platformAccessEnabled: false, platformUsername: '', notes: '',
})
const form = ref(emptyForm())

async function load() {
  technicians.value = await getTechniciansForManager(props.parkId)
}
onMounted(load)

const activeTechnicians = computed(() => technicians.value.filter((t) => t.active))
const inactiveTechnicians = computed(() => technicians.value.filter((t) => !t.active))
const activeCount = computed(() => activeTechnicians.value.length)
const availableCount = computed(() => activeTechnicians.value.filter((t) => t.status === 'AVAILABLE').length)
const workingCount = computed(() => activeTechnicians.value.filter((t) => t.status === 'WORKING').length)

function initials(name) { return name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase() }
function specialtyLabel(t) { return t.specialty === 'OTHER' ? (t.specialtyOther || 'Otro') : (SPECIALTY_LABELS[t.specialty] || '—') }
function capacityClass(pct) { return pct >= 85 ? 'tmp-card-capacity-fill--red' : pct >= 60 ? 'tmp-card-capacity-fill--amber' : 'tmp-card-capacity-fill--green' }

function toggleDay(d) {
  const i = form.value.availableDays.indexOf(d)
  if (i >= 0) form.value.availableDays.splice(i, 1)
  else form.value.availableDays.push(d)
}

const canSave = computed(() => !!form.value.name && !!form.value.phone)

function onOpenAddForm() {
  formMode.value = 'add'
  editingId.value = null
  form.value = emptyForm()
  view.value = 'form'
}

function startEdit(t) {
  formMode.value = 'edit'
  editingId.value = t.id
  form.value = {
    name: t.name, cargo: t.cargo || '', avatarColor: t.avatarColor,
    specialty: t.specialty || '', specialtyOther: t.specialtyOther || '',
    phone: t.phone || '', email: t.email || '',
    maxConcurrentTickets: t.maxConcurrentTickets ?? 3, contractType: t.contractType,
    hourlyRate: t.hourlyRate,
    availableDays: [...(t.availableDays || [])],
    availableStartTime: t.availableStartTime || '', availableEndTime: t.availableEndTime || '',
    flexibleSchedule: !!t.flexibleSchedule,
    platformAccessEnabled: !!t.platformAccessEnabled, platformUsername: t.platformUsername || '',
    notes: t.notes || '',
  }
  view.value = 'form'
}

function onBackFromForm() {
  view.value = 'list'
}

async function onSave() {
  if (formMode.value === 'edit') {
    await updateTechnician(editingId.value, form.value)
  } else {
    await createTechnician(form.value)
  }
  view.value = 'list'
  await load()
  emit('updated')
}

// --- Menú de fila ---
const rowMenuOpenId = ref(null)
const rowMenuPos = ref({ top: '0px', left: '0px' })
const rowMenuTechnician = computed(() => technicians.value.find((t) => t.id === rowMenuOpenId.value) || null)

function toggleRowMenu(id, event) {
  if (rowMenuOpenId.value === id) { rowMenuOpenId.value = null; return }
  const rect = event.currentTarget.getBoundingClientRect()
  rowMenuPos.value = { top: rect.bottom + 4 + 'px', left: rect.right - 200 + 'px' }
  rowMenuOpenId.value = id
}

function onEditFromMenu() {
  const t = rowMenuTechnician.value
  rowMenuOpenId.value = null
  if (t) startEdit(t)
}

async function onToggleActive() {
  const t = rowMenuTechnician.value
  rowMenuOpenId.value = null
  if (!t) return
  await updateTechnician(t.id, { active: !t.active })
  await load()
  emit('updated')
}

function onStub(label) {
  rowMenuOpenId.value = null
  console.log(`TODO: ${label} — pendiente de construir`)
}

function handleOutsideClick(e) {
  if (!e.target.closest('.tmp-card-menu-btn') && !e.target.closest('.tmp-row-menu')) rowMenuOpenId.value = null
}
onMounted(() => window.addEventListener('click', handleOutsideClick))
onUnmounted(() => window.removeEventListener('click', handleOutsideClick))
</script>

<style scoped>
.tmp-panel {
  flex: 0 0 35%;
  max-width: 35%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid var(--border-active);
  border-radius: 10px;
  overflow: hidden;
  margin: 0.75rem 1.5rem 1.5rem 0;
  min-height: 0;
}

.tmp-header {
  flex-shrink: 0;
  height: 56px;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0 1.25rem;
  border-bottom: 1px solid #f1f5f9;
}

.tmp-title {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 15px;
  color: #1e3a5f;
}

.tmp-counts {
  font-family: var(--font-body);
  font-size: 12px;
  color: #64748b;
  margin-left: auto;
  margin-right: 0.5rem;
}

.tmp-back-btn {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
}

.tmp-close-btn {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  margin-left: auto;
}

.tmp-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 1rem 1.25rem;
}

.tmp-content::-webkit-scrollbar { width: 6px; }
.tmp-content::-webkit-scrollbar-thumb { background: var(--border-active); border-radius: 3px; }
.tmp-content::-webkit-scrollbar-track { background: transparent; }

.tmp-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 3rem 1rem;
  text-align: center;
}

.tmp-empty-icon { font-size: 40px; color: #cbd5e1; }
.tmp-empty-title { font-family: var(--font-body); font-size: 14px; font-weight: 500; color: #64748b; }
.tmp-empty-sub { font-family: var(--font-body); font-size: 12px; color: #94a3b8; max-width: 220px; }

.tmp-card {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  height: 72px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-left: 3px solid;
  border-radius: 8px;
  padding: 0 12px;
  margin-bottom: 0.5rem;
}

.tmp-card--available { border-left-color: #10b981; }
.tmp-card--working { border-left-color: #f0a500; }
.tmp-card--inactive { border-left-color: #94a3b8; opacity: 0.6; }

.tmp-card-avatar-wrap { position: relative; flex-shrink: 0; }

.tmp-card-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tmp-card-presence {
  position: absolute;
  bottom: -1px;
  right: -1px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 2px solid #ffffff;
}

.tmp-card-presence--available { background: #10b981; }
.tmp-card-presence--working { background: #f0a500; }
.tmp-card-presence--inactive { background: #94a3b8; }

.tmp-card-info { flex: 0 0 50%; min-width: 0; }
.tmp-card-name { font-family: var(--font-body); font-size: 13px; font-weight: 500; color: #1e3a5f; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tmp-card-specialty { font-family: var(--font-body); font-size: 11px; color: #94a3b8; }

.tmp-card-metrics { flex: 0 0 25%; display: flex; flex-direction: column; gap: 0.2rem; min-width: 0; }
.tmp-card-count { font-family: var(--font-body); font-size: 11px; font-weight: 500; color: #1e3a5f; white-space: nowrap; }
.tmp-card-capacity-bar { height: 4px; width: 60px; background: #e2e8f0; border-radius: 2px; overflow: hidden; }
.tmp-card-capacity-fill { height: 100%; }
.tmp-card-capacity-fill--green { background: #10b981; }
.tmp-card-capacity-fill--amber { background: #f0a500; }
.tmp-card-capacity-fill--red { background: #e84545; }

.tmp-card-actions { margin-left: auto; }
.tmp-card-menu-btn { background: none; border: none; color: #94a3b8; cursor: pointer; display: flex; }

.tmp-row-menu {
  position: fixed;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  min-width: 200px;
  z-index: 1000;
  padding: 0.3rem;
}

.tmp-row-menu button {
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  font-family: var(--font-body);
  font-size: 12px;
  color: var(--text-content);
  padding: 0.4rem 0.5rem;
  cursor: pointer;
  border-radius: 5px;
}

.tmp-row-menu button:hover { background: #f8fafc; }

.tmp-divider {
  display: flex;
  align-items: center;
  text-align: center;
  border-top: 1px solid #f1f5f9;
  margin: 0.75rem 0;
  padding-top: 0.5rem;
}

.tmp-divider span {
  font-family: var(--font-body);
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  color: #94a3b8;
  margin: 0 auto;
}

.tmp-form-section {
  margin-bottom: 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.tmp-form-group-title {
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #94a3b8;
}

.tmp-input,
.tmp-textarea {
  width: 100%;
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--text-content);
  background: #f8f9fc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.5rem 0.6rem;
  box-sizing: border-box;
}

.tmp-field-row { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; }
.tmp-field-row-2col { display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem; }
.tmp-field-label { font-family: var(--font-body); font-size: 12px; color: #64748b; }

.tmp-stepper { display: flex; align-items: center; gap: 0.5rem; }
.tmp-stepper button {
  width: 24px; height: 24px; border-radius: 50%; border: 1px solid #e2e8f0; background: #ffffff;
  color: #1e3a5f; cursor: pointer; font-size: 14px; display: flex; align-items: center; justify-content: center;
}
.tmp-stepper span { font-family: var(--font-mono); font-size: 13px; color: #1e3a5f; min-width: 16px; text-align: center; }

.tmp-pills { display: flex; gap: 0.4rem; }
.tmp-pill {
  background: #ffffff; border: 1px solid #e2e8f0; border-radius: 20px; padding: 0.3rem 0.7rem;
  font-family: var(--font-body); font-size: 12px; color: #64748b; cursor: pointer;
}
.tmp-pill--active { background: #eff6ff; border-color: #3b82f6; color: #3b82f6; font-weight: 500; }

.tmp-days { display: flex; gap: 0.3rem; }
.tmp-day-btn {
  width: 28px; height: 28px; border-radius: 50%; border: 1px solid #e2e8f0; background: #ffffff;
  color: #64748b; font-family: var(--font-body); font-size: 11px; cursor: pointer;
}
.tmp-day-btn--active { background: #3b82f6; border-color: #3b82f6; color: #ffffff; }

.tmp-toggle-row { display: flex; align-items: center; gap: 0.4rem; font-family: var(--font-body); font-size: 12px; color: #1e3a5f; }

.tmp-hint { font-family: var(--font-body); font-size: 10px; color: #94a3b8; font-style: italic; }

.tmp-footer {
  flex-shrink: 0;
  height: 48px;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0 1.25rem;
  border-top: 1px solid #e2e8f0;
}

.tmp-add-btn {
  width: 100%;
  background: #3b82f6;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 0.5rem;
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.tmp-cancel-btn {
  background: none;
  border: none;
  color: #3b82f6;
  font-family: var(--font-body);
  font-size: 13px;
  cursor: pointer;
}

.tmp-save-btn {
  flex: 1;
  background: #3b82f6;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 0.5rem;
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.tmp-save-btn:disabled { opacity: 0.4; cursor: default; }

.tmp-color-picker {
  display: flex;
  gap: 0.35rem;
}

.tmp-color-swatch {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid transparent;
  cursor: pointer;
}

.tmp-color-swatch--active {
  border-color: #1e3a5f;
}
</style>